import {joinStrings} from '../../src/utils/array.ts';
import {getDateInTimezone} from '../../src/utils/dates.ts';
import {div} from '../../src/utils/html.ts';
import type {ArticleCardLayoutRecord, DigitalEditionItemData, Item, ListItemData, Page} from '../../types/types.ts';
import {LoadResourceError} from '../lib/errors.ts';
import type {LayoutParser} from '../lib/layout-parser.ts';
import type {Store} from '../lib/store.ts';
import {Article, type ArticleData} from '../models/article.ts';
import {executeSingleItemSource} from '../services/content.ts';
import {ContentAdapter} from './content-adapter.ts';

const CPPIdRegex = /\d+-\d+-\d+/;

export class DnContentAdapter extends ContentAdapter {
	readonly locale = 'no-NO';
	readonly timezone = 'Europe/Oslo';
	singleItemSourceId = '67ceef2aa29083566e69ee44';
	getListQueryParams(_publishDate: string) {
		return {
			'params[limit]': 50
		};
	}

	getSectionForPrintItem(printPage: Page, item: Item, parser: LayoutParser) {
		const printPageSection = printPage.properties?.section;

		return parser.getSection() || printPageSection || item.data.section || item.data.category || '';
	}

	getStoryForPrintItem(printPage: Page, item: Item, parser: LayoutParser): string {
		const topic = item.data.topic && item.data.topic !== '0' ? item.data.topic : null;
		return String(printPage.properties?.hotTopic || topic || parser.getStory());
	}

	createEditionBrief(
		data: DigitalEditionItemData,
		store: Store,
		articleIdsInEdition: string[],
		editionTitle: string
	): Article {
		return new Article(
			{
				id: data.id,
				sourceId: this.singleItemSourceId,
				objectId: null,
				data,
				publishedAt: data.publishedDate,
				layout: {
					html: `<article>${data.intro}</article>`.replaceAll(/href=["'](.*?)["']/g, (substring) => {
						const articleIdMatches = substring.match(CPPIdRegex);
						if (!articleIdMatches || !articleIdsInEdition.includes(articleIdMatches[0])) {
							return substring;
						}

						return `${substring} data-action="go-to-article" data-action-value="${articleIdMatches[0]}"`;
					})
				},
				title: data.title,
				pretitle: editionTitle,
				intro: '',
				story: 'Intro',
				size: data.intro ? Math.round(data.intro.length / 40) : 0 // Roughly count number of lines
			},
			store.getItemState(`${this.singleItemSourceId}:${data.id}`)
		);
	}

	formatListItems(
		items: ListItemData[],
		groupBy: string | null,
		date: string | null,
		store: Store,
		sourceId?: string,
		timezone?: string
	): Article[] {
		const list: Article[] = [];
		const currentSourceId = sourceId || this.singleItemSourceId;
		items.forEach((data) => {
			if (!CPPIdRegex.test(data.id)) {
				return;
			}

			if (data.id !== '2-1-1805602') {
				// return list;
			}

			const articleData = normalizeListResult(data as unknown as CPPListItem, groupBy, currentSourceId);
			const article = new Article(articleData, store.getItemState(`${currentSourceId}:${articleData.id}`));

			if (!article || (date && getDateInTimezone(article.publishedAt, timezone || 'UTC') !== date)) {
				return;
			}

			list.push(article);
		});

		return list;
	}

	async ensurePublishedAt(item: CPPListItem): Promise<void> {
		if (!CPPIdRegex.test(item.id)) {
			return;
		}

		switch (item.type) {
			case 'lantern-article':
				item.publishedAt = item.publishedDate;
				return;
		}

		if (!item.publishedAt) {
			const data = await executeSingleItemSource<CPPArticleData>(this.singleItemSourceId, item.id);
			if (data.status !== 200) {
				throw new LoadResourceError('Error executing single item query source', data);
			}
			item.publishedAt = data.body.published_at;
		}
	}

	async loadItem(
		article: Article,
		groupBy: string | null,
		_sortOrder: number | undefined,
		store: Store,
		sourceId?: string
	): Promise<Article | null> {
		if (!CPPIdRegex.test(article.id)) {
			return null;
		}

		const currentSourceId = sourceId || article.sourceId || this.singleItemSourceId;
		const data = await executeSingleItemSource<CPPArticleData>(currentSourceId, article.id);

		if (data.status !== 200) {
			return null;
		}

		const relations = await loadRelations(data.body, currentSourceId);
		const articleData = normalize(data.body, groupBy, relations, currentSourceId);

		return new Article(articleData, store.getItemState(`${currentSourceId}:${article.id}`));
	}

	async loadSingleItem(articleId: string, sourceId: string | undefined, store: Store): Promise<Article> {
		const currentSourceId = sourceId || this.singleItemSourceId;
		const data = await executeSingleItemSource<CPPArticleData>(currentSourceId, articleId);
		if (data.status !== 200) {
			throw new LoadResourceError(`Could not load article ${articleId} from source ${currentSourceId}`, data);
		}

		const relations = await loadRelations(data.body, currentSourceId);
		const articleData = normalize(data.body, null, relations, currentSourceId);
		const storeKey = `${currentSourceId}:${articleId}`;
		return new Article(articleData, store.getItemState(storeKey));
	}
}

async function loadRelations(data: CPPArticleData, sourceId: string) {
	const factBoxes = await fetchRelations<CPPFactBox>(
		getRelationsByType<CPPFactBoxRef>(data.relations?.inline, 'factbox'),
		sourceId
	);

	const wines = await fetchRelations<CPPWine>(
		getRelationsByType<CPPWineRef>(data.relations?.related_articles, 'wine'),
		sourceId
	);

	const embeds = await fetchRelations<CPPEmbed>(
		getRelationsByType<CPPEmbedRef>(data.relations?.inline, 'embed'),
		sourceId
	);

	return {factBoxes, wines, embeds};
}

function getRelationsByType<T>(relations: CPPRef[], relType: string) {
	if (!relations) {
		return [];
	}

	return relations.filter(({type}) => type === relType) as T[];
}

async function fetchRelations<T>(relations: {id: string}[], sourceId: string): Promise<T[]> {
	return relations.reduce(async (acc: Promise<T[]>, curr) => {
		const list = await acc;
		const response = await executeSingleItemSource<T>(sourceId, curr.id);
		if (response.status === 200) {
			list.push(response.body);
		}
		return list;
	}, Promise.resolve([]));
}

function normalizeListResult(data: CPPListItem, groupBy: string | null, sourceId: string) {
	const intervals = ['00:00', '06:00', '12:00', '18:00'];

	if (data.type === 'cpp-article-dn') {
		const date = new Date(data.publishedAt);
		return {
			id: data.id,
			sourceId,
			objectId: null,
			publishedAt: data.publishedAt,
			sort: date.getTime(),
			groupBy: groupBy ?? intervals[Math.floor(date.getHours() / 6)],
			data,
			layout: {
				html: ''
			},
			size: 0,
			pretitle: '',
			title: data.title,
			intro: '',
			sourceUrl: data.url,
			topic: data.kicker
		};
	}

	const date = new Date(data.publishedDate);
	return {
		id: data.id,
		sourceId,
		objectId: null,
		publishedAt: data.publishedDate,
		sort: date.getTime(),
		groupBy: groupBy ?? intervals[Math.floor(date.getHours() / 6)],
		data,
		layout: {
			html: ''
		},
		size: 0,
		pretitle: '',
		title: data.title,
		intro: data.leadText,
		sourceUrl: data.url,
		topic: data.topic
	};
}

function normalize(
	data: CPPArticleData,
	groupBy: string | null,
	relations: {factBoxes: CPPFactBox[]; wines: CPPWine[]; embeds: CPPEmbed[]},
	sourceId: string
): ArticleData {
	const date = new Date(data.published_at);
	const intervals = ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00'];
	return {
		id: data.id,
		sourceId,
		objectId: null,
		publishedAt: data.published_at,
		groupBy: groupBy ?? intervals[Math.floor(date.getHours() / 4)],
		sort: date.getTime(),
		data: {},
		layout: {
			html:
				renderTopic() +
				renderFactBoxes(relations.factBoxes) +
				processBody(data.fields.body) +
				renderWines(relations.wines) +
				renderImages()
		},
		byline: {
			html: renderBylines(),
			items: data.byline.authors
		},
		sourceUrl: data.canonical_url,
		pretitle: '',
		title: data.fields.title,
		intro: data.fields.lead_text,
		size: Math.round(data.fields.body.length / 40), // Roughly count number of lines
		section: getSection(),
		story: data.topics?.map((t) => t.title).join(', ')
	};

	function getSection() {
		const mainCategory = data.categories.find((cat) => cat.default)?.title;
		if (mainCategory !== 'standard') {
			return mainCategory;
		}

		return data.topics[0]?.title || mainCategory;
	}

	function processBody(body: string) {
		return body.replaceAll(
			/<link href=["']\/v1\/content\/(\d+-\d+-\w+)["'] rel="inline" source="(\w+)" ref=["'](\d+)["']( class="([a-z-]+)")? \/>/g,
			(_match, id, source, inlineRefId, _, className) => {
				const embed = relations.embeds.find((embed) => embed.id === id);
				if (embed) {
					return renderEmbed(embed);
				}
				const inlineRef = data.relations.inline.find((ref) => ref.ref_id === inlineRefId);
				if (inlineRef) {
					return renderInlineRef(inlineRef);
				}

				console.log('Skipped inline ref', id, source, inlineRefId, className);
				return '';
			}
		);
	}

	function renderEmbed(embed: CPPEmbed) {
		const {fields} = embed;
		if (/red-video-article/.test(embed.fields.embed_content)) {
			return '';
		}

		if (fields.title && fields.thumbnail) {
			return `<div class="embed">
	<div class="embed__title">${fields.title?.split(' | ')[0]}</div>
	<div class="embed__image"><img src="${fields.thumbnail}" /></div>
	</div>`;
		}

		if (fields.title === 'Høydespiller') {
			return '';
		}

		if (fields.embed_content) {
			return `<div class="embed">${fields.embed_content}</div>`;
		}

		return '';
	}

	function renderInlineRef(ref: CPPRef) {
		if (ref.type === 'picture') {
			// These are filtered out for figure rendering
			if (ref.fields.layout !== 'small') {
				return '';
			}
			return renderImage(ref, true);
		}

		return '';
	}

	function renderTopic() {
		return div(div(data.topics?.map((t) => t.title).join(', '), 'topic'), '');
	}

	function renderFactBoxes(factBoxes: CPPFactBox[]) {
		return div(
			factBoxes
				.map((f) => {
					return div(
						div(f.fields.title, 'fact-box-header') + div(f.fields.body, 'fact-box-body'),
						'fact-box'
					);
				})
				.join('\n'),
			''
		);
	}

	function renderWines(wines: CPPWine[]) {
		return div(
			wines
				.sort((a, b) => Number(b.fields.score) - Number(a.fields.score))
				.map((w) => {
					return div(
						div(w.fields.original_title, 'wine__title') +
							div(`${w.fields.type}, ${w.fields.score} poeng, ${w.fields.price}`, 'wine__facts') +
							div(`${w.fields.taste_notes} ${w.fields.tester}.`, 'wine__taste-notes') +
							div(
								`<a href="${w.canonical_url}" target="dn-vin">${w.fields.product_id}</a>`,
								'wine__link'
							),
						'wine'
					);
				})
				.join('\n'),
			''
		);
	}

	function renderBylines() {
		const usedIds = new Set();

		const authors = data.byline.authors?.reduce((acc: CPPAuthor[], curr: CPPAuthor) => {
			if (usedIds.has(curr.id)) {
				return acc;
			}

			usedIds.add(curr.id);
			acc.push(curr);
			return acc;
		}, []);

		if (!authors) {
			return '';
		}

		if (
			data.byline.metadata.sign_off_text &&
			!authors.some(({metadata}) => metadata?.profileImage || metadata?.profileTitle)
		) {
			const authorsString = joinStrings(authors.map(({name}) => name));
			return div(
				div(authorsString, 'byline__authors') + div(data.byline.metadata.sign_off_text, 'byline__signoff'),
				`byline bylines--${authors.length} byline--names-only`
			);
		}

		if (authors.length > 3) {
			const authorsString = joinStrings(authors.map(({name}) => name));
			return div(div(authorsString, 'byline__authors'), `byline bylines--${authors.length} byline--names-only`);
		}

		return div(
			authors
				.map((author) => {
					const profileTitle = author.metadata?.profileTitle || author.metadata?.alternativeBylineProfileText;
					return `
<div class="byline--web">
	${div(author.metadata?.profileImage && `<img src="${author.metadata.profileImage}"/>`, 'byline__photo')}
	<div class="byline__text">
		<div class="byline__name">${author.name}</div>
		${div(profileTitle, 'byline__title')}
	</div>
</div>
`;
				})
				.join(''),
			`byline bylines--${authors.length}`
		);
	}

	function renderImages() {
		if (!data.relations) {
			return '';
		}
		// console.log(data.id, data.relations);
		const relations = [
			...(data.relations.lead_assets || []),
			...(data.relations.inline || [])
			// ...(data.relations.other_photos || [])
		].filter((ref) => ref.type === 'picture' && ref.fields?.layout !== 'small') as CPPImageRef[];

		// console.log(relations);
		const usedIds = new Set();
		return relations
			.map((imageRef) => {
				if (usedIds.has(imageRef.id)) {
					return '';
				}

				usedIds.add(imageRef.id);

				return div(renderImage(imageRef));
			})
			.join('');
	}

	function renderImage(imageRef: CPPImageRef, inline = false) {
		const caption = !(imageRef.fields?.caption || imageRef.fields?.credit)
			? ''
			: div(div(imageRef.fields.caption, 'caption') + div(imageRef.fields.credit, 'credit'), 'caption-wrapper');

		const format = getFormat(imageRef);
		const size = getSize(imageRef);
		const imageUrl = getImageUrl(imageRef, format);

		return `
	<div class="picture picture--${format} picture--${size}${inline ? ' picture--inline' : ''}"
		data-alignment="${imageRef.fields?.alignment || ''}" data-layout="${imageRef.fields?.layout || ''}" data-ratio="${imageRef.fields?.ratio || ''}" >
		<div class="image-wrapper">
				<img src="${imageUrl}"
				data-src-default="${imageUrl}"
				data-src-full="${imageRef.metadata.image_original}"
				data-src-square="${imageRef.metadata.image_representaitons.one_one}"
				data-src-wide="${imageRef.metadata.image_representaitons.two_one}"
				data-src-tall="${imageRef.metadata.image_representaitons.five_seven}"
				>
		</div>
        ${caption}
    </div>
`;
	}

	function getFormat(imageRef: CPPImageRef) {
		// console.log(data.id, imageRef.fields.alignment, imageRef.fields.layout, imageRef.fields.ratio);
		if (imageRef.fields?.alignment === 'left') {
			if (imageRef.fields.layout === 'small') {
				return 'square';
			}
		}

		return 'landscape';
	}

	function getSize(imageRef: CPPImageRef) {
		if (imageRef.fields?.layout === 'small') {
			return 'small';
		}

		return 'large';
	}

	function getImageUrl(imageRef: CPPImageRef, format: string) {
		if (format === 'square') {
			return imageRef.metadata.image_representaitons.one_one;
		}

		return imageRef.metadata.image_representaitons.three_two;
	}
}

type CPPListItem = CPPLanternArticle | CPPArticleDN;

type CPPLanternArticle = {
	id: string;
	guid: string;
	title: string;
	type: 'lantern-article';
	url: string;
	images: [
		{
			id: string;
			url: string;
			crops: string;
			ref: string;
			aoi: {
				focus: {
					x: number;
					y: number;
				};
				width: number;
				height: number;
				origin: string;
				x: number;
				y: number;
			};
			baseWidth: number;
			baseHeight: number;
		}
	];
	leadText: string;
	paid: boolean;
	topic: string;
	articleType: string;
	status: string;
	duoTopImage: boolean;
	crosshead: string;
	duoTopImageCaption: string;
	publication: string;
	publishedDate: string;
	publishedAt?: string;
	isBig: boolean;
	layout: string;
	fontType: string;
};

type CPPArticleDN = {
	id: string;
	footer: string;
	kicker: string;
	title: string;
	type: 'cpp-article-dn';
	lanternType: string;
	url: string;
	features: {
		source: string;
		'title-size': string;
		badge: string;
		'image-format': string;
		'text-position': string;
		'image-crop-mode': string;
	};
	backgroundColor: string;
	image: {
		url: string;
		id: string;
		thumbnailUrl: string;
	};
	publication: string;
	publishedAt: string;
	priority: string;
	mobileImagePosition: string;
	mobileBPlus: boolean;
	'title-font': string;
	imageHide: boolean;
	imagePosition: string;
};

export type CPPArticleData = {
	id: string;
	type: string;
	url: string;
	cardLayouts: {
		desktop: ArticleCardLayoutRecord;
		mobile: ArticleCardLayoutRecord;
	};
	canonical_url: string;
	sub_type: string;
	published_at: string;
	publication: IdTitle;
	fields: {
		body: string;
		exclude_from_news: string;
		lead_text: string;
		original_title: string;
		paid: string;
		title: string;
		working_title: string;
	};
	categories: Category[];
	topics: IdTitle[];
	tags: IdTitle[];
	byline: {
		authors: CPPAuthor[];
		metadata: {
			published_location: string;
			sign_off_text: string;
		};
	};
	relations: {
		related_articles: Array<CPPRef>;
		mandatory_factboxes: Array<CPPFactBoxRef>;
		inline: Array<CPPRef>;
		lead_assets: CPPImageRef[];
		other_photos: CPPImageRef[];
	};
	guid: string;
};

type IdTitle = {
	id: string;
	title: string;
};

type Category = {
	id: string;
	title: string;
	default: boolean;
};

type CPPAuthor = {
	id: string;
	type: string;
	name: string;
	email: string;
	metadata: {
		alternativeBylineProfileText: string;
		profileTitle: string;
		profileImage: string;
	};
};

type CPPRef = CPPEmbedRef | CPPFactBoxRef | CPPImageRef | CPPVideoRef | CPPNewsRef;

type CPPEmbedRef = {
	id: string;
	type: 'embed';
	url: string;
	byline: object;
	guid: string;
	ref_id: string;
};

type CPPFactBoxRef = {
	id: string;
	type: 'factbox';
	url: string;
	fields: {
		alignment: string;
	};
	byline: object;
	guid: string;
	ref_id: string;
};

type CPPFactBox = {
	id: string;
	state: string;
	type: 'factbox';
	url: string;
	canonical_url: string;
	publication: IdTitle;
	fields: {
		body: string;
		title: string;
		original_title: string;
	};
	byline: object;
	guid: string;
};

type CPPEmbed = {
	id: string;
	state: string;
	type: 'embed';
	url: string;
	canonical_url: string;
	publication: IdTitle;
	fields: {
		articleUsage: string;
		embed_content: string;
		isUsedInArticles: string;
		label: string;
		originalUrl: string;
		original_title: string;
		processBatchId: string;
		provider: string;
		thumbnail: string;
		title: string;
	};
	byline: object;
	guid: string;
};

type CPPWineRef = {
	id: string;
	type: 'wine';
	url: string;
	fields: {
		title: string;
	};
	byline: object;
	guid: string;
	ref_id: string;
};

type CPPWine = {
	id: string;
	state: string;
	type: string;
	url: string;
	canonical_url: string;
	first_published_at: string;
	published_at: string;
	publication: IdTitle;
	fields: {
		acid: string;
		alcohol: string;
		barcode: string;
		country: string;
		district: string;
		drink_from: string;
		drink_to: string;
		grape: string;
		importer: string;
		latest_reviewed: string;
		meal: string;
		name: string;
		original_title: string;
		packaging_material_id: string;
		price: string;
		producer: string;
		product_id: string;
		score: string;
		size: string;
		sold_at: string;
		sugar: string;
		taste_notes: string;
		test_date: string;
		tester: string;
		type: string;
		year: string;
	};
	byline: object;
	relations: {
		lead_assets: CPPImageRef[];
	};
	guid: string;
};

type CPPImageRef = {
	id: string;
	state: string;
	type: 'picture';
	url: string;
	fields: {
		ratio: string;
		alignment: string;
		caption: string;
		credit: string;
		pointOfInterest: string;
		layout: string;
	};
	byline: object;
	guid: string;
	ref_id: string;
	metadata: CPPImageMetadata;
};

type CPPImageMetadata = {
	image_metadata: string;
	image_original: string;
	image_representaitons: {
		five_seven: string;
		five_six: string;
		five_two: string;
		four_three: string;
		free: string;
		one_one: string;
		one_two: string;
		sixteen_nine: string;
		sixteen_six: string;
		three_two: string;
		two_one: string;
	};
};

type CPPVideoRef = {
	id: string;
	state: string;
	type: 'video';
	url: string;
	fields: {
		alignment: string;
		description: string;
		exclude_from_news: string;
		original_title: string;
		title: string;
	};
	byline: object;
	relations: {
		lead_assets: CPPImageRef[];
	};
	guid: string;
	ref_id: string;
	metadata: {
		duration: string;
		jwplayer_video_id: string;
		source: string;
	};
};

type CPPNewsRef = {
	id: string;
	type: 'news';
	url: string;
	fields: {
		title: string;
	};
	byline: object;
	guid: string;
	ref_id: string;
};
